import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import PoweredByFooter from "../components/PoweredByFooter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { 
  ArrowLeft, Warehouse, Package, Search, Check, X, AlertTriangle,
  CheckCircle, XCircle, HelpCircle, Filter, Download, Upload, Plus, Trash2, Archive
} from "lucide-react";
import { useIsArchived, ArchivedBanner } from "../components/ArchivedBanner";

// Status typer for lager
const LAGER_STATUS = [
  { id: 'returneret', label: 'Returneret', icon: CheckCircle, color: 'text-green-500 bg-green-500/10' },
  { id: 'mangler', label: 'Mangler', icon: XCircle, color: 'text-red-500 bg-red-500/10' },
  { id: 'beskadiget', label: 'Beskadiget', icon: AlertTriangle, color: 'text-orange-500 bg-orange-500/10' },
  { id: 'lager', label: 'Til lager', icon: Archive, color: 'text-blue-500 bg-blue-500/10' },
  { id: 'afventer', label: 'Afventer', icon: HelpCircle, color: 'text-muted-foreground bg-muted' },
];

export default function Lager({ user }) {
  const navigate = useNavigate();
  const isArchived = useIsArchived();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterOmraade, setFilterOmraade] = useState("");
  const [editingItem, setEditingItem] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newItem, setNewItem] = useState({ produktinformation: '', kategori: '', omraade: '', antal: 1, enhed: 'stk', bemærkning: '' });

  const token = localStorage.getItem("token");
  const eventId = localStorage.getItem("selectedEventId");
  const eventName = localStorage.getItem("selectedEventName");
  const isAdmin = user?.role === 'admin' || user?.role === 'admin_assistant';

  useEffect(() => {
    loadLagerData();
  }, [eventId]);

  const loadLagerData = async () => {
    if (!eventId || !token) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/events/${eventId}/lager`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setItems(res.data || []);
    } catch (error) {
      console.error('Fejl ved hentning af lagerdata:', error);
      // Hvis endpoint ikke findes endnu, vis tomme data
      setItems([]);
    }
    setLoading(false);
  };

  const updateItem = async (itemId, updatedData) => {
    try {
      await axios.put(
        `${API}/events/${eventId}/lager/${itemId}`,
        updatedData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Opdater lokalt
      setItems(items.map(i => i.id === itemId ? { ...i, ...updatedData } : i));
      toast.success('Item opdateret');
      setEditingItem(null);
    } catch (error) {
      toast.error('Fejl ved opdatering');
      console.error(error);
    }
  };

  // Quick status update (til knapperne)
  const updateItemStatus = async (itemId, newStatus) => {
    await updateItem(itemId, { lager_status: newStatus });
  };

  // Tilføj nyt lager-item
  const addLagerItem = async () => {
    if (!newItem.produktinformation) {
      toast.error('Produktnavn er påkrævet');
      return;
    }
    try {
      await axios.post(
        `${API}/events/${eventId}/lager`,
        newItem,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Lager-item tilføjet');
      setShowAddForm(false);
      setNewItem({ produktinformation: '', kategori: '', omraade: '', antal: 1, enhed: 'stk', bemærkning: '' });
      loadLagerData();
    } catch (error) {
      toast.error('Fejl ved tilføjelse');
      console.error(error);
    }
  };

  // Slet/skjul lager-item
  const [confirmDelete, setConfirmDelete] = useState(null);
  
  const deleteLagerItem = async (itemId) => {
    setConfirmDelete(itemId);
  };
  
  const confirmDeleteItem = async () => {
    if (!confirmDelete) return;
    try {
      await axios.delete(
        `${API}/events/${eventId}/lager/${confirmDelete}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Item fjernet fra lager');
      setConfirmDelete(null);
      loadLagerData();
    } catch (error) {
      toast.error('Kunne ikke fjerne item');
      console.error(error);
      setConfirmDelete(null);
    }
  };

  const exportToCSV = () => {
    const headers = ['Produkt', 'Kategori', 'Område', 'Antal', 'Status', 'Bemærkning', 'Kilde'];
    const rows = filteredItems.map(item => [
      item.produktinformation,
      item.kategori,
      item.omraade,
      item.antal,
      LAGER_STATUS.find(s => s.id === item.lager_status)?.label || 'Afventer',
      item.lager_bemærkning || '',
      item.source === 'manuel' ? 'Manuel' : 'Bestilling'
    ]);
    
    const csv = [headers, ...rows].map(row => row.map(cell => `"${cell || ''}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `lager_${eventName}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Eksporteret til CSV');
  };

  // Filtrering
  const uniqueOmraader = [...new Set(items.map(i => i.omraade).filter(Boolean))].sort();
  
  const filteredItems = items.filter(item => {
    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      if (!(item.produktinformation || '').toLowerCase().includes(s) &&
          !(item.kategori || '').toLowerCase().includes(s) &&
          !(item.leverandoer || '').toLowerCase().includes(s)) {
        return false;
      }
    }
    if (filterStatus && item.lager_status !== filterStatus) return false;
    if (filterOmraade && item.omraade !== filterOmraade) return false;
    return true;
  });

  // Statistik
  const stats = {
    total: items.length,
    returneret: items.filter(i => i.lager_status === 'returneret').length,
    mangler: items.filter(i => i.lager_status === 'mangler').length,
    beskadiget: items.filter(i => i.lager_status === 'beskadiget').length,
    lager: items.filter(i => i.lager_status === 'lager').length,
    afventer: items.filter(i => !i.lager_status || i.lager_status === 'afventer').length,
  };

  const progressPercent = items.length > 0 
    ? Math.round(((stats.returneret + stats.mangler + stats.beskadiget + stats.lager) / stats.total) * 100)
    : 0;

  return (
    <div className="min-h-screen bg-background">
      {/* Bekræftelses-dialog for sletning */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
          <div className="bg-card border border-border rounded-t-xl sm:rounded-lg p-6 w-full sm:max-w-md">
            <div className="flex items-center gap-3 mb-4">
              <AlertTriangle className="h-6 w-6 text-destructive" />
              <h2 className="text-lg font-bold">Fjern fra lager?</h2>
            </div>
            <p className="text-muted-foreground mb-6">Er du sikker på du vil fjerne dette item fra lageret? Bestillingen påvirkes ikke.</p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setConfirmDelete(null)}>Annuller</Button>
              <Button variant="destructive" onClick={confirmDeleteItem} data-testid="confirm-delete-lager-btn">Fjern</Button>
            </div>
          </div>
        </div>
      )}
      {/* Header */}
      <header className="border-b border-border bg-card sticky top-0 z-40">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div className="flex items-center gap-2">
              <Warehouse className="h-5 w-5 text-primary" />
              <h1 className="text-lg font-bold">Lager / Nedpakning</h1>
            </div>
            <span className="text-muted-foreground text-sm">- {eventName}</span>
          </div>
          
          {/* Kompakte stats i header */}
          <div className="flex items-center gap-4 text-sm">
            <span><strong>{stats.total}</strong> total</span>
            <span className="text-green-500"><strong>{stats.returneret}</strong> ret.</span>
            <span className="text-red-500"><strong>{stats.mangler}</strong> mangler</span>
            <span className="text-orange-500"><strong>{stats.beskadiget}</strong> besk.</span>
            <span className="text-blue-500"><strong>{stats.lager}</strong> lager</span>
            <span className="text-muted-foreground"><strong>{stats.afventer}</strong> afv.</span>
            <span className="text-primary font-bold">{progressPercent}%</span>
          </div>
          
          <div className="flex items-center gap-2">
            {!isArchived && (
              <Button variant="outline" size="sm" onClick={() => setShowAddForm(true)}>
                <Plus className="h-4 w-4 mr-2" />Tilføj
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={exportToCSV}>
              <Download className="h-4 w-4 mr-2" />Eksporter
            </Button>
          </div>
        </div>
        
        {/* Fremgang og filtre - sticky under header */}
        <div className="container mx-auto px-4 pb-3">
          {/* Progress bar */}
          <div className="mb-3">
            <div className="h-2 bg-muted rounded-full overflow-hidden">
              <div 
                className="h-full bg-primary transition-all duration-500"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          </div>

          {/* Filtre */}
          <div className="flex flex-wrap gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Søg produkt, kategori..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="h-10 px-3 rounded-md border border-input bg-background text-sm"
            >
              <option value="">Alle status</option>
              {LAGER_STATUS.map(s => (
                <option key={s.id} value={s.id}>{s.label}</option>
              ))}
            </select>
            <select
              value={filterOmraade}
              onChange={(e) => setFilterOmraade(e.target.value)}
              className="h-10 px-3 rounded-md border border-input bg-background text-sm"
            >
              <option value="">Alle områder</option>
              {uniqueOmraader.map(o => (
                <option key={o} value={o}>{o}</option>
              ))}
            </select>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6">
        {isArchived && <ArchivedBanner />}

        {/* Tabel */}
        {loading ? (
          <div className="text-center py-12 text-muted-foreground">Indlæser...</div>
        ) : filteredItems.length === 0 ? (
          <div className="text-center py-12">
            <Package className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
            <p className="text-muted-foreground">
              {items.length === 0 
                ? 'Ingen materialer fundet. Importer materialer fra Materiel-siden først.'
                : 'Ingen materialer matcher dine filtre.'}
            </p>
          </div>
        ) : (
          <div className="bg-card border border-border rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/30">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium">Produkt</th>
                    <th className="px-4 py-3 text-left font-medium">Kategori</th>
                    <th className="px-4 py-3 text-left font-medium">Område</th>
                    <th className="px-4 py-3 text-center font-medium">Antal</th>
                    <th className="px-4 py-3 text-center font-medium">Kilde</th>
                    <th className="px-4 py-3 text-center font-medium">Status</th>
                    <th className="px-4 py-3 text-left font-medium">Bemærkning</th>
                    <th className="px-4 py-3 w-40"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filteredItems.map(item => {
                    const status = LAGER_STATUS.find(s => s.id === item.lager_status) || LAGER_STATUS[4];
                    const StatusIcon = status.icon;
                    const isEditing = editingItem?.id === item.id;

                    return (
                      <tr key={item.id} className={`hover:bg-accent/5 ${isEditing ? 'bg-primary/5' : ''} ${item.source === 'manuel' ? 'bg-yellow-500/5' : ''}`}>
                        <td className="px-4 py-3">
                          {isEditing ? (
                            <Input
                              value={editingItem.produktinformation || ''}
                              onChange={(e) => setEditingItem({ ...editingItem, produktinformation: e.target.value })}
                              className="h-8"
                            />
                          ) : (
                            <span className="font-medium">{item.produktinformation}</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {isEditing ? (
                            <Input
                              value={editingItem.kategori || ''}
                              onChange={(e) => setEditingItem({ ...editingItem, kategori: e.target.value })}
                              className="h-8"
                            />
                          ) : (
                            <span className="text-muted-foreground">{item.kategori || '-'}</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {isEditing ? (
                            <Input
                              value={editingItem.omraade || ''}
                              onChange={(e) => setEditingItem({ ...editingItem, omraade: e.target.value })}
                              className="h-8"
                            />
                          ) : (
                            <span>{item.omraade || '-'}</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {isEditing ? (
                            <Input
                              type="number"
                              value={editingItem.antal || 1}
                              onChange={(e) => setEditingItem({ ...editingItem, antal: parseInt(e.target.value) || 1 })}
                              className="h-8 w-16 text-center"
                              min="1"
                            />
                          ) : (
                            <span>{item.antal || '-'}</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={`text-xs px-2 py-0.5 rounded ${item.source === 'manuel' ? 'bg-yellow-500/20 text-yellow-500' : 'bg-muted text-muted-foreground'}`}>
                            {item.source === 'manuel' ? 'Manuel' : 'Bestilling'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          {isEditing ? (
                            <select
                              value={editingItem.lager_status || 'afventer'}
                              onChange={(e) => setEditingItem({ ...editingItem, lager_status: e.target.value })}
                              className="h-8 px-2 rounded border border-input bg-background text-sm"
                            >
                              {LAGER_STATUS.map(s => (
                                <option key={s.id} value={s.id}>{s.label}</option>
                              ))}
                            </select>
                          ) : (
                            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${status.color}`}>
                              <StatusIcon className="h-3 w-3" />
                              {status.label}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {isEditing ? (
                            <Input
                              value={editingItem.lager_bemærkning || ''}
                              onChange={(e) => setEditingItem({ ...editingItem, lager_bemærkning: e.target.value })}
                              placeholder="Tilføj bemærkning..."
                              className="h-8"
                            />
                          ) : (
                            <span className="text-muted-foreground">{item.lager_bemærkning || '-'}</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {isEditing ? (
                            <div className="flex gap-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => updateItem(item.id, {
                                  produktinformation: editingItem.produktinformation,
                                  kategori: editingItem.kategori,
                                  omraade: editingItem.omraade,
                                  antal: editingItem.antal,
                                  lager_status: editingItem.lager_status,
                                  lager_bemærkning: editingItem.lager_bemærkning
                                })}
                                className="h-7 w-7 p-0 text-green-500"
                              >
                                <Check className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setEditingItem(null)}
                                className="h-7 w-7 p-0"
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          ) : (
                            <div className="flex gap-1">
                              {LAGER_STATUS.slice(0, 4).map(s => {
                                const Icon = s.icon;
                                const isActive = item.lager_status === s.id;
                                return (
                                  <Button
                                    key={s.id}
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => updateItemStatus(item.id, s.id)}
                                    className={`h-7 w-7 p-0 ${isActive ? s.color.split(' ')[0] : 'text-muted-foreground hover:text-foreground'}`}
                                    title={s.label}
                                    disabled={isArchived}
                                  >
                                    <Icon className="h-4 w-4" />
                                  </Button>
                                );
                              })}
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setEditingItem({ ...item })}
                                className="h-7 px-2 text-xs"
                                disabled={isArchived}
                              >
                                Rediger
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => deleteLagerItem(item.id)}
                                className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                                title="Fjern fra lager"
                                data-testid="delete-lager-item-btn"
                                disabled={isArchived}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
        <PoweredByFooter />
      </main>
      
      {/* Tilføj lager-item dialog */}
      {showAddForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-lg p-6 max-w-lg w-full">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold">Tilføj lager-item</h3>
              <button onClick={() => setShowAddForm(false)} className="p-1 hover:bg-muted rounded">
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <p className="text-sm text-muted-foreground mb-4">
              Tilføj items der ikke var en del af en bestilling (f.eks. gaver, spontane køb).
            </p>
            
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium block mb-1">Produkt *</label>
                <Input
                  value={newItem.produktinformation}
                  onChange={(e) => setNewItem({...newItem, produktinformation: e.target.value})}
                  placeholder="F.eks. 2 stk. reoler"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium block mb-1">Kategori</label>
                  <Input
                    value={newItem.kategori}
                    onChange={(e) => setNewItem({...newItem, kategori: e.target.value})}
                    placeholder="F.eks. Opbevaring"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium block mb-1">Område</label>
                  <Input
                    value={newItem.omraade}
                    onChange={(e) => setNewItem({...newItem, omraade: e.target.value})}
                    placeholder="F.eks. Lager"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium block mb-1">Antal</label>
                  <Input
                    type="number"
                    value={newItem.antal}
                    onChange={(e) => setNewItem({...newItem, antal: parseInt(e.target.value) || 1})}
                    min="1"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium block mb-1">Enhed</label>
                  <Input
                    value={newItem.enhed}
                    onChange={(e) => setNewItem({...newItem, enhed: e.target.value})}
                    placeholder="stk"
                  />
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium block mb-1">Bemærkning</label>
                <Input
                  value={newItem.bemærkning}
                  onChange={(e) => setNewItem({...newItem, bemærkning: e.target.value})}
                  placeholder="F.eks. Gave fra sponsor"
                />
              </div>
              
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="ghost" onClick={() => setShowAddForm(false)}>Annuller</Button>
                <Button onClick={addLagerItem}>
                  <Plus className="h-4 w-4 mr-2" />
                  Tilføj
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
